import { useEffect, useState } from "react";
import platformApi from "@/lib/platformApi";
import { formatApiErrorDetail } from "@/lib/api";
import { LogoMark } from "@/components/logo/AetriaLogo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { PasswordInput } from "@/components/ui/password-input";
import { Skeleton } from "@/components/ui/skeleton";
import CreateTenantDialog from "@/components/platform/CreateTenantDialog";
import TenantDetailPanel from "@/components/platform/TenantDetailPanel";
import RevenuePanel from "@/components/platform/RevenuePanel";
import PlatformManualPanel from "@/components/platform/PlatformManualPanel";
import GlobalUmsPanel from "@/components/platform/GlobalUmsPanel";
import ObservatoryPanel from "@/components/platform/ObservatoryPanel";
import AlertsBell from "@/components/platform/AlertsBell";
import TrialApprovalsPanel from "@/components/platform/TrialApprovalsPanel";
import RootTenantOverview from "@/components/platform/RootTenantOverview";
import { Loader2, LogOut, Building2, BarChart3, BookOpen, Users, Telescope, Eye, ShieldCheck, Clock, Monitor, Cloud, FileDown, Languages } from "lucide-react";
import { Checkbox } from "@/components/ui/checkbox";
import TrustedDevicesDialog from "@/components/TrustedDevicesDialog";
import CloudConnectionsPanel from "@/components/platform/CloudConnectionsPanel";
import TranslationsPanel from "@/components/platform/TranslationsPanel";
import BrochureStatsCard from "@/components/platform/BrochureStatsCard";

const STATUS_STYLE = {
  active: "bg-teal-50 border-teal-200 text-teal-700",
  trial: "bg-blue-50 border-blue-200 text-blue-700",
  pending_approval: "bg-amber-50 border-amber-200 text-amber-700",
  rejected: "bg-red-50 border-red-200 text-red-700",
  past_due: "bg-orange-50 border-orange-200 text-orange-700",
  suspended: "bg-red-50 border-red-200 text-red-700",
  cancelled: "bg-slate-100 border-slate-200 text-slate-500",
};

function PlatformLogin({ onLogin }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [remember, setRemember] = useState(false);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setError("");
    try {
      const { data } = await platformApi.post("/auth/login", { email, password, remember });
      localStorage.setItem("aetria_platform_token", data.access_token);
      onLogin(data.user);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || "Login failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#1E293B]" data-testid="platform-login-page">
      <form onSubmit={submit} className="w-full max-w-sm bg-white p-10">
        <div className="flex items-center gap-3 mb-8">
          <LogoMark size={40} />
          <div>
            <p className="text-[15px] font-bold text-[#1E293B] leading-none">AETRIA <span className="text-[var(--brand-accent-ink)]">OS</span></p>
            <p className="text-[9px] uppercase tracking-[0.22em] text-slate-400 mt-1">Platform Console</p>
          </div>
        </div>
        <div className="space-y-3">
          <Input data-testid="platform-login-email" type="text" placeholder="Email or username"
            value={email} onChange={(e) => setEmail(e.target.value)} className="rounded-none" autoComplete="username" />
          <PasswordInput data-testid="platform-login-password" placeholder="Password"
            value={password} onChange={(e) => setPassword(e.target.value)} className="rounded-none" autoComplete="current-password" />
        </div>
        {error && <p className="text-[11px] text-red-600 mt-3" data-testid="platform-login-error">{error}</p>}
        <label className="flex items-center gap-2.5 mt-4 cursor-pointer select-none" data-testid="platform-remember-label">
          <Checkbox checked={remember} onCheckedChange={(v) => setRemember(Boolean(v))}
            data-testid="platform-remember-checkbox" className="rounded-none" />
          <span className="text-[11.5px] text-slate-600">Trust this device for 30 days</span>
        </label>
        <Button data-testid="platform-login-submit" type="submit" disabled={busy || !email || !password}
          className="w-full mt-4 rounded-none bg-[#1E293B] hover:bg-slate-700 text-white h-10">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : null} Sign in to Platform
        </Button>
        <p className="text-[9px] text-slate-400 mt-6 text-center uppercase tracking-[0.14em]">
          Super-admin access only — tenant workspaces sign in at their own domain
        </p>
        <div className="mt-5 flex items-center justify-center gap-2.5" data-testid="platform-login-announcement">
          <span className="h-px w-5 shrink-0" style={{ background: "var(--brand-accent)" }} />
          <p className="text-[10.5px] uppercase tracking-[0.16em] text-slate-400 text-center">
            The multi-tenant AI workforce operating system
          </p>
          <span className="h-px w-5 shrink-0" style={{ background: "var(--brand-accent)" }} />
        </div>
      </form>
    </div>
  );
}

export default function PlatformConsolePage() {
  const [user, setUser] = useState(null); // null=checking, false=login, obj=in
  const [stats, setStats] = useState(null);
  const [tenants, setTenants] = useState(null);
  const [selected, setSelected] = useState(null);
  const [mrr, setMrr] = useState(null);
  const [tab, setTab] = useState("tenants");
  const [overviewTenant, setOverviewTenant] = useState(null);
  const [pendingCount, setPendingCount] = useState(0);
  const [devicesOpen, setDevicesOpen] = useState(false);

  useEffect(() => {
    if (!localStorage.getItem("aetria_platform_token")) { setUser(false); return; }
    platformApi.get("/auth/me").then((r) => setUser(r.data)).catch(() => setUser(false));
  }, []);

  const load = () => {
    platformApi.get("/stats").then((r) => setStats(r.data));
    platformApi.get("/tenants").then((r) => setTenants(r.data.tenants));
    platformApi.get("/billing/overview").then((r) => setMrr(r.data.mrr_usd)).catch(() => {});
    platformApi.get("/trial-approvals").then((r) => setPendingCount(r.data.count)).catch(() => {});
  };
  useEffect(() => { if (user && user !== false) load(); }, [user]); // eslint-disable-line

  if (user === null) return <div className="min-h-screen bg-[#1E293B]" />;
  if (user === false) return <PlatformLogin onLogin={setUser} />;

  const isRoot = user.role === "root_super_admin";
  const tabList = [
    ["tenants", "Tenants", Building2],
    ["approvals", "Approvals", Clock],
    ["revenue", "Revenue", BarChart3],
    ["cloud", "Cloud Connections", Cloud],
    ...(isRoot ? [["ums", "Global UMS", Users], ["observatory", "Observatory", Telescope]] : []),
    ["languages", "Languages", Languages],
    ["manual", "Manual", BookOpen],
  ];

  const kpis = stats ? [
    ["Total tenants", stats.total_tenants, "platform-kpi-total"],
    ["Active", stats.active_tenants, "platform-kpi-active"],
    ["Employees (all)", stats.total_employees, "platform-kpi-employees"],
    ["MRR (USD)", mrr === null ? "—" : `$${Number(mrr).toLocaleString()}`, "platform-kpi-mrr"],
  ] : [];

  return (
    <div className="min-h-screen bg-[#F8FAFC]" data-testid="platform-console">
      <header className="bg-[#1E293B] text-white">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <LogoMark size={32} />
            <div>
              <p className="text-[14px] font-bold leading-none">AETRIA <span className="text-[var(--brand-accent-ink)]">OS</span></p>
              <p className="text-[8px] uppercase tracking-[0.24em] text-slate-400 mt-0.5">Platform Console</p>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <AlertsBell />
            {isRoot && (
              <span className="text-[9px] font-mono uppercase px-1.5 py-0.5 border border-amber-400/50 text-amber-300 flex items-center gap-1" data-testid="root-badge">
                <ShieldCheck className="h-3 w-3" /> root
              </span>
            )}
            <span className="text-[11px] font-mono text-slate-300" data-testid="platform-user-email">{user.email}</span>
            <a data-testid="platform-brochure-btn" title="Sales brochure (PDF)"
              href={`${process.env.REACT_APP_BACKEND_URL}/api/marketing/brochure.pdf`}
              target="_blank" rel="noreferrer"
              className="text-slate-400 hover:text-white"><FileDown className="h-4 w-4" /></a>
            <button data-testid="platform-devices-btn" title="Trusted devices"
              onClick={() => setDevicesOpen(true)}
              className="text-slate-400 hover:text-white"><Monitor className="h-4 w-4" /></button>
            <button data-testid="platform-logout-btn"
              onClick={async () => {
                try { await platformApi.post("/auth/logout"); } catch { /* ignore */ }
                localStorage.removeItem("aetria_platform_token"); setUser(false);
              }}
              className="text-slate-400 hover:text-white"><LogOut className="h-4 w-4" /></button>
          </div>
        </div>
      </header>

      <TrustedDevicesDialog open={devicesOpen} onOpenChange={setDevicesOpen}
        client={platformApi} basePath="/auth" title="Your trusted devices" />

      <main className="max-w-6xl mx-auto px-6 py-8 space-y-6">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {kpis.map(([label, value, id]) => (
            <div key={id} className="bg-white border border-slate-200 px-5 py-4" data-testid={id}>
              <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-400">{label}</p>
              <p className="text-[24px] font-bold text-[#1E293B] font-mono mt-1">{value}</p>
            </div>
          ))}
        </div>

        <div className="flex items-center gap-1 border-b border-slate-200" data-testid="platform-tabs">
          {tabList.map(([id, label, Icon]) => (
            <button key={id} onClick={() => setTab(id)} data-testid={`platform-tab-${id}`}
              className={`flex items-center gap-2 px-5 py-2.5 text-[12px] font-bold uppercase tracking-[0.12em] border-b-2 -mb-px transition-colors ${
                tab === id ? "text-[#1E293B]" : "text-slate-400 border-transparent hover:text-slate-600"}`}
              style={tab === id ? { borderBottomColor: "var(--brand-accent)" } : {}}>
              <Icon className="h-4 w-4" style={tab === id ? { color: "var(--brand-accent-ink)" } : {}} />
              {label}
              {id === "approvals" && pendingCount > 0 && (
                <span data-testid="approvals-badge"
                  className="ml-0.5 min-w-[16px] h-[16px] px-1 flex items-center justify-center text-[9px] font-mono font-bold text-white bg-amber-500 rounded-full">
                  {pendingCount}
                </span>
              )}
            </button>
          ))}
        </div>

        {tab === "revenue" && <div className="space-y-6"><RevenuePanel /><BrochureStatsCard /></div>}
        {tab === "manual" && <PlatformManualPanel />}
        {tab === "languages" && <TranslationsPanel />}
        {tab === "approvals" && <TrialApprovalsPanel isRoot={isRoot} onChanged={load} />}
        {tab === "cloud" && <CloudConnectionsPanel isRoot={isRoot} />}
        {tab === "ums" && isRoot && <GlobalUmsPanel tenants={tenants || []} />}
        {tab === "observatory" && isRoot && <ObservatoryPanel tenants={tenants || []} onOpenTenant={setOverviewTenant} />}
        {tab === "tenants" && (
          <>
        <div className="flex items-center justify-between">
          <h2 className="text-[13px] font-bold uppercase tracking-[0.14em] text-[#1E293B] flex items-center gap-2">
            <Building2 className="h-4 w-4" style={{ color: "var(--brand-accent-ink)" }} /> Tenants
          </h2>
          <CreateTenantDialog onCreated={load} />
        </div>

        {!tenants ? <Skeleton className="h-48 w-full rounded-none" /> : (
          <div className="bg-white border border-slate-200 overflow-x-auto">
            <table className="w-full text-[12px]" data-testid="tenants-table">
              <thead>
                <tr className="text-left text-[10px] uppercase tracking-wider text-slate-400 border-b border-slate-200 bg-slate-50">
                  {["Subdomain", "Organization", "Plan", "Status", "Employees", "Created", ...(isRoot ? [""] : [])].map((h, i) => (
                    <th key={i} className="px-4 py-2.5 font-semibold">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {tenants.map((t) => (
                  <tr key={t.subdomain} data-testid={`tenant-row-${t.subdomain}`}
                    onClick={() => setSelected(t.subdomain)}
                    className={`cursor-pointer hover:bg-slate-50 ${selected === t.subdomain ? "bg-teal-50/40" : ""}`}>
                    <td className="px-4 py-2.5 font-mono font-semibold text-[#1E293B]">{t.subdomain}</td>
                    <td className="px-4 py-2.5 text-slate-700">{t.org_name}</td>
                    <td className="px-4 py-2.5 text-slate-500 capitalize">{t.plan}</td>
                    <td className="px-4 py-2.5">
                      <span className={`text-[9px] font-mono uppercase px-1.5 py-0.5 border ${STATUS_STYLE[t.status] || ""}`}>
                        {t.status}
                      </span>
                    </td>
                    <td className="px-4 py-2.5 font-mono">
                      {t.employees_count}{t.employee_cap ? ` / ${t.employee_cap}` : ""}
                    </td>
                    <td className="px-4 py-2.5 text-slate-400 font-mono text-[11px]">{String(t.created_at).slice(0, 10)}</td>
                    {isRoot && (
                      <td className="px-4 py-2.5">
                        <button data-testid={`root-overview-btn-${t.subdomain}`} title="Root overview (read-only)"
                          onClick={(e) => { e.stopPropagation(); setOverviewTenant(t.subdomain); }}
                          className="text-slate-400 hover:text-[#1E293B]"><Eye className="h-4 w-4" /></button>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {selected && <TenantDetailPanel subdomain={selected} onChanged={load} />}
          </>
        )}
      </main>
      <RootTenantOverview subdomain={overviewTenant} onClose={() => setOverviewTenant(null)} />
    </div>
  );
}
